import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import appStore from '@/store/app';
import ordersStore from '@/store/orders';
import productsStore from '@/store/products';
import erpStore from '@/store/erp';
import CardComponent from '@/components/CardComponent';
import CustomToolbar from '@/components/CustomToolbar';
import { ProductDetail, BaseProduct, TypeOfOperation, ProductAvailability, Statistic, ProductStatus } from '@/types';
import { debounce } from '@/core/debounce';
// import { faGrinTongueSquint } from '@fortawesome/free-solid-svg-icons';
// import tracer from '@/core/tracer';

@Component({
   components: { CardComponent, CustomToolbar },
})
export default class ProductDetails extends Vue {

   // Injectectd from url
   @Prop({ type: String, required: true, default: '' }) private readonly type!: string;
   @Prop({ type: String, required: true, default: '' }) private readonly pharmacode!: string;
   @Prop({ type: Number, required: false, default: 1 }) private readonly quantity!: number;

   private activeTab = 0;
   private quantityToUse = this.quantity || 0;
   private availabilityLoaded = false;
   private availabilityLoading = false;
   private availabilityLoadingValue = 0;
   private quantityChanged = false;
   private addToHistorical = true;

   private selectedStatistic: Statistic | null = null;
   private pageSize = appStore.pageSize;
   private pageNumber = 1;
   private sortField = 'date';
   private sortDirection = 'asc';
   private statisticsLoaded = false;
   private ignoreQuantityChanged = false;

   private debouncedFetchProductAsync = async (): Promise<void> => undefined;
   private debouncedFetchProductAvailabilityAsync = async (): Promise<void> => undefined;

   get loading(): boolean {
      return appStore.isLoading;
   }

   get product(): ProductDetail {
      return productsStore.product;
   }

   get availability(): ProductAvailability {
      return productsStore.availability;
   }

   get statistics(): Statistic[] {
      return erpStore.statistics;
   }

   get totalStatistics(): number {
      return erpStore.totalStatistics;
   }

   get fanCode(): string | undefined {
      if (this.product.wholesalerInfo && this.product.wholesalerInfo.fan) {
         return this.product.wholesalerInfo.fan;
      }
      return undefined;
   }

   get stockInFridgeWithTemperature(): string | undefined {
      if (!this.product) return '';

      if (this.product.isStockFridge) {
         return 'Sì' + (this.product.stockTemperature ? ' - (' + this.product.stockTemperature + '°)' : '');
      } else {
         return 'No';
      }
   }

   get quantityAlreadyInOrder(): number {

      if (!ordersStore.cart || !ordersStore.cart.lines || ordersStore.cart.lines.length === 0) {
         return 0;
      }
      const orderLine = ordersStore.cart.lines.filter((line) =>
         line.productType === this.product.type && line.pharmacode === this.product.pharmacode);

      if (orderLine && orderLine.length > 0) {
         const quantityInOrder = orderLine[0].quantity;
         if (quantityInOrder && quantityInOrder > 0) {
            this.ignoreQuantityChanged = true;
            if (!this.quantityChanged) {
               this.quantityToUse = orderLine[0].quantity;
            }
         }

         return quantityInOrder || 0;
      }

      return 0;
   }

   private async created() {

      this.debouncedFetchProductAsync = debounce(this.fetchProductAsync, 800);
      this.debouncedFetchProductAvailabilityAsync = debounce(this.fetchProductAvailabilityAsync, 1200);

      await this.fetchProductAsync();

      if (!ordersStore.isCartLoaded) {
         await ordersStore.fetchCartAsync({});
      }
   }

   private async mounted() {
      await appStore.activateMenuById({ rootId: 100, subMenuId: 101 });
   }

   // We are watching pharmacode changes cause routing inside the same view
   // does not reload so we need to reload everything.
   @Watch('pharmacode', { immediate: true, deep: false })
   private async onProductReplaced(value: string, previousValue?: string) {
      if (previousValue === undefined || value === previousValue) return;
      this.pageNumber = 1;
      this.availabilityLoaded = false;
      this.statisticsLoaded = false;
      await this.fetchProductAsync();
   }

   @Watch('quantityToUse', { immediate: true, deep: false })
   private async onQuantityChanged(value: number, previousValue?: number) {
      if (this.ignoreQuantityChanged) {
         this.ignoreQuantityChanged = false;
         return;
      }
      if (previousValue === undefined) return;
      if (!value || value <= 0 || value === previousValue) {
         return;
      }

      this.quantityChanged = true;

      await this.debouncedFetchProductAsync();
   }

   private async onTabChange(tab: number) {
      this.activeTab = tab;
      if (tab === 1 && !this.statisticsLoaded) {
         this.fetchStatisticsAsync();
      }
   }

   private back() {
      this.$router.go(-1);
   }

   private async fetchProductAsync() {
      this.availabilityLoadingValue = 10;
      await productsStore.fetchProductAsync({ type: this.type, pharmacode: this.pharmacode, quantity: this.quantityToUse })
         .then(async () => {
            if (!this.availabilityLoaded) {
               this.availabilityLoadingValue = 30;
               await this.debouncedFetchProductAvailabilityAsync();
            }
         });
   }

   private async fetchProductAvailabilityAsync() {
      this.availabilityLoading = true;
      this.availabilityLoadingValue = 50;
      await productsStore.fetchProductAvailabilityAsync({ type: this.type, pharmacode: this.pharmacode })
         .then(() => {
            this.availabilityLoaded = true;
            this.availabilityLoadingValue = 100;
            this.availabilityLoading = false;
         });
   }

   private async addToCart() {

      if (this.product) {

         if (this.product.status === ProductStatus.Discontinued) {
            if (this.product.replacementProduct) {
               appStore.warning({
                  message: `ATTENZIONE: il prodotto è stato sostituito con ${this.product.replacementProduct.name} | ${this.product.replacementProduct.pharmacode}`, timeout: 7000,
               });
            } else {
               appStore.warning({ message: 'ATTENZIONE: il prodotto è stato sostituito.', timeout: 7000 });
            }
            return;
         }

         if (this.product.type && this.product.pharmacode) {
            await ordersStore.addProductToCartAsync(
               {
                  product: this.product as BaseProduct,
                  quantity: this.quantityToUse,
                  operation: TypeOfOperation.ReplaceQuantity,
                  addToHistorical: this.addToHistorical,
               })
               .then(() => {
                  this.quantityChanged = false;
               });
         }
      }
   }

   private async showReplacementProduct() {
      if (this.product.replacementProduct) {
         await this.$router.replace({ name: 'product', params: { type: this.product.replacementProduct.type, pharmacode: this.product.replacementProduct.pharmacode } });
      }
   }

   private async onSort(field: string, direction: string) {
      this.sortField = field;
      this.sortDirection = direction;
      await this.fetchStatisticsAsync();
   }

   private async onPageChange(page: number) {
      this.pageNumber = page;
      await this.fetchStatisticsAsync();
   }

   private selectStatistic(row: Statistic, oldRow: Statistic | null): void {
      this.selectedStatistic = row;
   }

   private async fetchStatisticsAsync() {
      if (this.fanCode) {
         await erpStore.fetchStatisticsAsync({ fan: this.fanCode, page: this.pageNumber, size: this.pageSize, sortField: this.sortField, sortDirection: this.sortDirection })
            .then(() => this.statisticsLoaded = true);
      }
   }
}
